import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetAdCategoriesQuery, useDeleteAdCategoryMutation, useToggleAdCategoryStatusMutation, useGetAdCategoryQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Image from '../../../components/Image';
import DateRangePicker from '../../../components/DateRangePicker';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';

// Format date as "d M, Y" (e.g., "27 Mar, 2023")
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const AdminAdCategories: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const parent_id = searchParams.get('parent_id');
  
  const [search, setSearch] = useState('');
  const [daterange, setDaterange] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useGetAdCategoriesQuery({ search, daterange, page, parent_id });
  const { data: parentCategoryData } = useGetAdCategoryQuery(parent_id, {
    skip: !parent_id,
  });
  const parentCategory = parentCategoryData?.data;
  const [deleteAdCategory] = useDeleteAdCategoryMutation();
  const [toggleAdCategoryStatus] = useToggleAdCategoryStatusMutation();

  // Reset to page 1 when search or daterange changes
  useEffect(() => {
    setPage(1);
  }, [search, daterange, parent_id]);

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      const newStatus = currentStatus ? false : true;
      const result = await toggleAdCategoryStatus({ id, status: newStatus }).unwrap();
      showSuccess(result.message || 'Ad Category status updated successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update ad category status. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirmAction(
      'Delete Ad Category',
      'Are you sure you want to delete this ad category?',
      'Yes, Delete'
    );
    
    if (confirmed) {
      try {
        const result = await deleteAdCategory(id).unwrap();
        showSuccess(result.message || 'Ad Category deleted successfully!');
        refetch();
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to delete ad category. Please try again.');
      }
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading ad categories</div>;
  }

  const categories = data?.data || { data: [], current_page: 1, last_page: 1 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {parentCategory ? `Subcategories of ${parentCategory.title}` : 'Ad Categories'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-categories'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/ad-categories'); }} className="text-muted text-hover-primary">Ad Categories</a>
                </li>
                {parentCategory && (
                  <>
                    <li className="breadcrumb-item">
                      <span className="bullet bg-gray-400 w-5px h-2px"></span>
                    </li>
                    <li className="breadcrumb-item text-muted">
                      {parentCategory.title}
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => { e.preventDefault(); }}>
                    <div className="d-flex align-items-center position-relative my-1">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-350px ps-15 me-5"
                        placeholder="Search Category Title.."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div className="position-relative d-flex align-items-center">
                        <DateRangePicker
                          value={daterange}
                          onChange={setDaterange}
                          placeholder="Date Range"
                        />
                        {(search || daterange) && (
                          <a href="#" onClick={(e) => { e.preventDefault(); setSearch(''); setDaterange(''); }} className="btn btn-sm btn-light-primary me-3">Clear</a>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
                <div className="card-toolbar gap-3">
                  {parent_id && (
                    <button 
                      className="btn btn-light" 
                      onClick={(e) => { 
                        e.preventDefault(); 
                        // If we are deep, maybe we want to go back to parent, but for now just go to root or previous
                        // Since we don't know the grandparent easily without more state/queries, just going back to root or using browser back is simplest.
                        // But let's try to go back to root if we are at level 1.
                        navigate('/admin/ad-categories'); 
                      }}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to Categories
                    </button>
                  )}
                  <a href="#" className="btn btn-base" onClick={(e) => { 
                    e.preventDefault(); 
                    const url = parent_id ? `/admin/ad-categories/add?parent_id=${parent_id}` : '/admin/ad-categories/add';
                    navigate(url); 
                  }}>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"></rect>
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"></rect>
                      </svg>
                    </span>
                    Add New {parent_id ? 'Subcategory' : 'Ad Category'}
                  </a>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>ICON</th>
                        <th>TITLE</th>
                        <th>TITLE ARABIC</th>
                        <th>STATUS</th>
                        <th>CREATED DATE</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {categories.data.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-10">
                            <span className="text-muted">No ad categories found</span>
                          </td>
                        </tr>
                      ) : (
                        categories.data.map((category: any, index: number) => (
                          <tr key={category.id}>
                            <td>{(categories.current_page - 1) * 15 + index + 1}</td>
                            <td>
                              {category.icon ? (
                                <div className="symbol symbol-50px">
                                  <Image src={category.icon} alt={category.title} className="w-100" />
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{category.title || 'N/A'}</td>
                            <td>{category.title_ar || 'N/A'}</td>
                            <td>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={category.status === true}
                                  onChange={() => handleToggleStatus(category.id, category.status)}
                                  style={{ cursor: 'pointer' }}
                                />
                              </div>
                            </td>
                            <td>{formatDate(category.created_at)}</td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                {['Agri Jobs', 'Farmland', 'Plants & Flowers','Agri Machinery','Agricultural Logistics'].includes(category.title) && 
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light-primary w-125px"
                                    onClick={() => navigate(`/admin/ad-categories?parent_id=${category.id}`)}
                                    title="Subcategories"
                                  >
                                    <i className="fas fa-sitemap"></i>
                                    Subcategories {category.sub_categories_count > 0 && `(${category.sub_categories_count})`}
                                  </button>
                                }
                                {!['Agri Jobs', 'Farmland', 'Plants & Flowers','Agri Machinery','Agricultural Logistics'].includes(category.title) && 
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light-info w-125px"
                                    onClick={() => navigate(`/admin/ad-categories/${category.id}/custom-fields`)}
                                    title="Custom Fields"
                                  >
                                    <i className="fas fa-list"></i>
                                    Custom Fields
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-light-info w-125px"
                                    onClick={() => navigate(`/admin/ad-categories/${category.id}/custom-filters`)}
                                    title="Custom Filters"
                                  >
                                    <i className="fas fa-filter"></i>
                                    Custom Filters
                                  </button>
                                </>
                                }
                                <ActionButtons
                                  onEdit={() => navigate(`/admin/ad-categories/${category.id}/edit`)}
                                  onDelete={() => handleDelete(category.id)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {categories.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={categories.current_page}
                      lastPage={categories.last_page}
                      total={categories.total || 0}
                      perPage={categories.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAdCategories;
